import { HttpClient } from "@utils/httpClient";
import type {
  GetTranslationResponse,
  ListTranslationsResponse,
  StartTranslationRequest,
  StartTranslationResponse,
  SubmitFeedbackRequest,
  CostRecordsResponse,
} from "@models/translation";

const baseUrl = (import.meta.env.VITE_TRANSLATION_API_BASE_URL ??
  import.meta.env.VITE_API_BASE_URL ?? "") as string;

const client = new HttpClient(baseUrl);

export const translationApi = {
  startTranslation(body: StartTranslationRequest): Promise<StartTranslationResponse> {
    return client.makeAuthenticatedRequest<StartTranslationResponse>(
      "/v1/startTranslation",
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  getTranslation(
    translationId: string,
    options: { targetLang?: string; verbose?: boolean } = {},
  ): Promise<GetTranslationResponse> {
    const params = new URLSearchParams({ translation_id: translationId });
    if (options.targetLang) params.set("target_lang", options.targetLang);
    if (options.verbose) params.set("verbose", "true");
    return client.makeAuthenticatedRequest<GetTranslationResponse>(
      `/v1/getTranslation?${params.toString()}`,
      { method: "GET" },
    );
  },

  submitFeedback(body: SubmitFeedbackRequest): Promise<{ status: string }> {
    return client.makeAuthenticatedRequest<{ status: string }>(
      "/v1/submitFeedback",
      { method: "POST", body: JSON.stringify(body) },
    );
  },

  /**
   * Fetch the authenticated user's single (text) translation history,
   * newest first. Scoped to type=single so batch jobs only appear in the
   * Batch tab (batch history is fetched separately via batchApi).
   */
  listTranslations(limit = 50): Promise<ListTranslationsResponse> {
    return client.makeAuthenticatedRequest<ListTranslationsResponse>(
      `/v1/translations?type=single&limit=${limit}`,
      { method: "GET" },
    );
  },

  /** Delete a single history entry by its content hash. */
  deleteHistoryEntry(contentHash: string): Promise<{ status: string; deleted: number }> {
    return client.makeAuthenticatedRequest<{ status: string; deleted: number }>(
      `/v1/translations/${encodeURIComponent(contentHash)}`,
      { method: "DELETE" },
    );
  },

  /** Clear the authenticated user's entire history. */
  clearHistory(): Promise<{ status: string; deleted: number }> {
    return client.makeAuthenticatedRequest<{ status: string; deleted: number }>(
      "/v1/translations",
      { method: "DELETE" },
    );
  },

  /**
   * Fetch cost records for a specific translation/batch job.
   * A job may have one record per target language; callers typically sum
   * total_cost_usd across the returned records for a per-job total.
   */
  getCostByJobId(jobId: string): Promise<CostRecordsResponse> {
    const params = new URLSearchParams({ job_id: jobId });
    return client.makeAuthenticatedRequest<CostRecordsResponse>(
      `/v1/costs?${params.toString()}`,
      { method: "GET" },
    );
  },
};
